import { useMemo } from 'react';
import { Modal } from '@/components/shared/Modal';
import { cn } from '@/lib/cn';
import type {
  EmailTemplateMaster,
  EmailTemplateMasterVersion,
} from '../api/platform-email-template.api';
import { diffVariables, extractVariables } from '../lib/template-validation';

interface Props {
  version: EmailTemplateMasterVersion | null;
  current: EmailTemplateMaster;
  onClose: () => void;
}

type LineKind = 'same' | 'added' | 'removed';
interface DiffLine {
  kind: LineKind;
  text: string;
}

/**
 * Longest-common-subsequence line diff. Small inputs (email bodies), so the
 * O(n*m) table is fine and keeps the result stable and readable.
 */
function diffLines(before: string, after: string): { left: DiffLine[]; right: DiffLine[] } {
  const a = before.split('\n');
  const b = after.split('\n');
  const m = a.length;
  const n = b.length;

  const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i]![j] =
        a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  const left: DiffLine[] = [];
  const right: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      left.push({ kind: 'same', text: a[i]! });
      right.push({ kind: 'same', text: b[j]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      left.push({ kind: 'removed', text: a[i]! });
      right.push({ kind: 'same', text: '' });
      i++;
    } else {
      left.push({ kind: 'same', text: '' });
      right.push({ kind: 'added', text: b[j]! });
      j++;
    }
  }
  while (i < m) {
    left.push({ kind: 'removed', text: a[i]! });
    right.push({ kind: 'same', text: '' });
    i++;
  }
  while (j < n) {
    left.push({ kind: 'same', text: '' });
    right.push({ kind: 'added', text: b[j]! });
    j++;
  }

  return { left, right };
}

function DiffPane({ title, lines }: { title: string; lines: DiffLine[] }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <div className="max-h-[380px] overflow-auto rounded-lg border border-border bg-muted/10">
        <pre className="min-w-max p-2 font-mono text-[11px] leading-relaxed">
          {lines.map((line, idx) => (
            <div
              key={idx}
              className={cn(
                'px-1',
                line.kind === 'added' && 'bg-green-500/15 text-green-800 dark:text-green-300',
                line.kind === 'removed' && 'bg-destructive/15 text-destructive',
              )}
            >
              {line.text || ' '}
            </div>
          ))}
        </pre>
      </div>
    </div>
  );
}

/**
 * Side-by-side version comparison (COMMUNICATION_DESIGN.md §6 "Compare").
 * Highlights added / removed lines and the variable delta between the selected
 * published version and the current master content.
 */
export function VersionCompare({ version, current, onClose }: Props) {
  const html = useMemo(
    () => (version ? diffLines(version.htmlBody, current.htmlBody) : null),
    [version, current.htmlBody],
  );

  const varDelta = useMemo(() => {
    if (!version) return { added: [], removed: [] };
    return diffVariables(
      extractVariables(version.subject, version.htmlBody, version.plainTextBody),
      extractVariables(current.subject, current.htmlBody, current.plainTextBody),
    );
  }, [version, current]);

  if (!version || !html) return null;

  const subjectChanged = version.subject !== current.subject;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Compare v${version.versionNumber} → current (v${current.currentVersion})`}
      size="2xl"
    >
      <div className="space-y-4">
        {/* Subject */}
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Subject {subjectChanged && <span className="text-amber-600">— modified</span>}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div
              className={cn(
                'rounded-lg border border-border px-3 py-2 text-xs',
                subjectChanged && 'bg-destructive/10',
              )}
            >
              {version.subject}
            </div>
            <div
              className={cn(
                'rounded-lg border border-border px-3 py-2 text-xs',
                subjectChanged && 'bg-green-500/10',
              )}
            >
              {current.subject}
            </div>
          </div>
        </div>

        {/* Variables */}
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Variables
          </p>
          {varDelta.added.length === 0 && varDelta.removed.length === 0 ? (
            <p className="text-xs text-muted-foreground">No variable changes.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {varDelta.added.map((v) => (
                <span
                  key={`a-${v}`}
                  className="rounded-full bg-green-500/15 px-2 py-0.5 font-mono text-[11px] text-green-700 dark:text-green-400"
                >
                  + {v}
                </span>
              ))}
              {varDelta.removed.map((v) => (
                <span
                  key={`r-${v}`}
                  className="rounded-full bg-destructive/15 px-2 py-0.5 font-mono text-[11px] text-destructive"
                >
                  − {v}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* HTML body */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <DiffPane title={`v${version.versionNumber} (published)`} lines={html.left} />
          <DiffPane title="Current" lines={html.right} />
        </div>
      </div>
    </Modal>
  );
}
